// PE-3169 -- the sign-in page's own pack: its stylesheet, plus the three pieces
// of behaviour the page has.
import "../stylesheets/auth/signin.scss";

// How long to hold the spinner before deciding the navigation is not coming.
// Esc, the browser's Stop button and a provisional navigation that fails outright
// all leave the document sitting here, and none of them fire an event it can see.
const GIVE_UP_MS = 12000;

// The split. This is $split in stylesheets/auth/signin.scss and the 62em in the
// <noscript> block in views/auth/signin.html.erb; the three have to agree, or the
// scene plays at a width where the stylesheet has not drawn it.
const DESKTOP = "(min-width: 62em)";

// Signing in hands off to accounts.google.com, so the click is the last thing
// this document does and there is nothing to await. Swapping the control for the
// spinner is the only feedback available. Enhancement only: without JS the link
// still navigates.
const wireLoadingSwap = (page) => {
  const link = page.querySelector("[data-gsi]");
  const status = page.querySelector("[data-gsi-status]");

  if (!link || !status) return;

  let timer = null;

  // The state is written with the attribute methods rather than through
  // `dataset`, here and below: the page belongs to the caller, and assigning to a
  // property of it trips the pack linter's parameter rules. Same attribute, same
  // result -- `[data-state="loading"]` is what the stylesheet and the specs read.
  const idle = () => {
    window.clearTimeout(timer);
    timer = null;
    page.removeAttribute("data-state");
    status.textContent = "";

    // Focus was parked on the status line because the link was hidden. Now that
    // the link is back, hand it over rather than leaving focus on an empty node.
    if (document.activeElement === status) link.focus();
  };

  link.addEventListener("click", (event) => {
    // A modified or non-primary click opens Google in a new tab or window and
    // leaves this document exactly where it is, so it must not dress itself as
    // leaving.
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    page.setAttribute("data-state", "loading");

    // The live region is in the document from load and empty: a screen reader
    // announces what is written into a region already in the accessibility tree,
    // not a region revealed with its text already inside.
    status.textContent = "Signing in";

    // Hiding the link would otherwise drop keyboard and screen-reader focus to
    // <body>, losing the reader's place on the page.
    status.focus();

    timer = window.setTimeout(idle, GIVE_UP_MS);
  });

  // The navigation is going ahead: nothing should still be counting down inside a
  // document that is about to be discarded or frozen into the bfcache mid-count.
  window.addEventListener("pagehide", () => {
    window.clearTimeout(timer);
    timer = null;
  });

  // Coming back from Google with the back button can restore this document from
  // the bfcache exactly as it was left: spinning, with nothing left to click.
  // pageshow fires on that restore as well as on a normal load.
  window.addEventListener("pageshow", idle);
};

// Once seen, never lost. The "Sign in" hint arrives on its own and then stays
// put: without this it would rewind to nothing the moment the pointer left, and
// the one piece of instruction on the page would come and go.
//
// The listeners sit on the wrapper rather than on the link because the Partner
// app overlays Google's iframe on that wrapper, where it would swallow every
// pointer event aimed at the control underneath. The two estates behave the same
// way for the same reason.
const wireHintLatch = (page) => {
  const control = page.querySelector(".wordmark-control");

  if (!control) return;

  const latch = () => control.classList.add("wm-hinted");

  // Both pointer and mouse families. pointerenter is the modern event, but not
  // everything that drives a browser emits one -- WebDriver, older assistive
  // tooling and legacy embeddings all still speak mouse. The class is
  // idempotent, so listening twice costs nothing and the first arrival wins.
  control.addEventListener("pointerenter", latch);
  control.addEventListener("mouseenter", latch);
  control.addEventListener("focusin", latch);
};

// M31 -- "the door ajar". The desktop scene needs script for two reasons: the
// door image must not be requested at widths where it is never drawn, and the
// swing must not start on a frame the door has not been decoded into. Below the
// split the scene does not exist, so neither does any of this.
//
// None of it is load-bearing. The page is already in its settled, usable pose
// before this runs -- see the inline arming script in views/auth/signin.html.erb
// -- so every path through here either improves that page or leaves it alone.
const mountDoorScene = (page) => {
  const scene = page.querySelector(".m31-scene");
  const leaf = scene && scene.querySelector(".m31-leaf");

  if (!scene || !leaf) return;

  // A second run would re-request the image and re-add a class that is already
  // there, part-way through the transition it started.
  if (scene.dataset.mounted === "true") return;
  scene.dataset.mounted = "true";

  const desktop = window.matchMedia(DESKTOP);

  const play = () => {
    // The one thing that has to happen at desktop and nowhere else.
    scene.querySelectorAll("source[data-srcset]").forEach((source) => {
      source.setAttribute("srcset", source.dataset.srcset);
      source.removeAttribute("data-srcset");
    });
    leaf.src = leaf.dataset.src;
    delete leaf.dataset.src;

    // Whether the entrance is running at all. It is not, if the reader asked for
    // reduced motion, or if the fail-open has already settled the page.
    const armed = page.classList.contains("pe-entrance-armed");
    let doorless = false;

    leaf
      .decode()
      .then(() => {
        // The scene is only painted once there is a door in it: an empty leaf
        // still draws its thickness strip, which would read as a stray dark
        // hairline beside the rail. This is also what puts the door on screen
        // for a reader who never armed the entrance.
        scene.dataset.door = "ready";
      })
      .catch((error) => {
        // Decoration must never cost anyone the sign-in. Leaving data-door unset
        // takes the empty scene off the page, and the settle below turns instant:
        // choreographing a 1100ms swing and a 700ms-deferred fade around a door
        // that will never arrive is 1.2s of blank white for nothing.
        doorless = true;
        // eslint-disable-next-line no-console
        console.warn(
          `PE-3169 sign-in: the desktop door failed to decode, opening without it. src=${
            leaf.currentSrc || leaf.src
          } error=${error && error.message}`
        );
      })
      .then(() => {
        if (!armed) return;

        if (doorless) {
          page.classList.add("m31-instant", "m31-open");
          return;
        }

        // Two frames: the first paints the closed door, the second is the one the
        // transition can start from. Adding the class any earlier transitions
        // from a pose that was never on screen, which is a jump, not a swing.
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => page.classList.add("m31-open"));
        });
      });
  };

  if (desktop.matches) {
    play();
    return;
  }

  // A window widened past the split after load still gets the scene it would
  // have had; without this it would inherit a shut door and a faded-out panel.
  const onChange = (event) => {
    if (!event.matches) return;
    desktop.removeEventListener("change", onChange);
    play();
  };

  desktop.addEventListener("change", onChange);
};

// The pack is deferred, so this runs after the document is parsed and before
// DOMContentLoaded fires -- the listener below is registered in time either way.
document.addEventListener("DOMContentLoaded", () => {
  const page = document.querySelector(".pe-login");

  if (!page) return;

  wireLoadingSwap(page);
  wireHintLatch(page);
  mountDoorScene(page);
});
